/**
 * UGC media strip — runtime half of snippets/product-ugc-strip.liquid.
 * Injects <video> from the tile's <template> once on screen, runs the muted
 * previews as a relay along the row (one clip at a time, poster + play
 * affordance where the device refuses), and expands a tile inline into a
 * stage + rail.
 *
 * Keep this one classic IIFE — no import()/export. Shopify's asset pipeline
 * transpiles to CommonJS, so a dynamic import() ships as require(), which is
 * undefined in the browser and rejects silently.
 */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-ugc]');
  if (!roots.length) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var hasIO = 'IntersectionObserver' in window;

  // Must match the grid-template-rows transition in the snippet's CSS.
  var TRANSITION_MS = 400;
  // A clip's turn ends when the clip does; these are the guard rails for when
  // it doesn't — a stalled download where `ended` never fires, and a clip long
  // enough to hold the row hostage. Re-armed from the real duration once known.
  var TURN_TIMEOUT_MS = 8000;
  var MAX_TURN_MS = 15000;

  Array.prototype.forEach.call(roots, initRoot);

  function initRoot(root) {
    var strip = root.querySelector('[data-ugc-strip]');
    var expander = root.querySelector('[data-ugc-expander]');
    if (!strip || !expander) return;

    var tiles = Array.prototype.slice.call(strip.querySelectorAll('[data-ugc-tile]'));
    var expanded = false;

    /* ------------------------------------------------------------------ */
    /* Strip scroller (more than 4 entries)                                */
    /* ------------------------------------------------------------------ */

    /* overflow-x is hidden on the strip (no swipe/scrollbar), so the arrows are
       the only way to move across it. Each hides itself once there's nothing
       further that way to reveal — prev at the head of the row, next at the
       tail. */
    var nextBtn = root.querySelector('[data-ugc-next]');
    var prevBtn = root.querySelector('[data-ugc-prev]');
    if (nextBtn || prevBtn) {
      var updateNav = function () {
        var max = strip.scrollWidth - strip.clientWidth;
        if (nextBtn) nextBtn.hidden = max <= 1 || strip.scrollLeft >= max - 1;
        if (prevBtn) prevBtn.hidden = strip.scrollLeft <= 1;
      };
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          strip.scrollBy({ left: strip.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      }
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          strip.scrollBy({ left: -strip.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      }
      strip.addEventListener('scroll', updateNav, { passive: true });
      window.addEventListener('resize', updateNav);
      updateNav();
    }

    /* ------------------------------------------------------------------ */
    /* Previews                                                            */
    /* ------------------------------------------------------------------ */

    /* Previews run as a relay, not a chorus: the first tile plays, and when its
       clip finishes the turn passes to the next tile along the row. Only one
       clip is ever decoding or downloading, which is also why there is no
       per-device preview budget here any more — five at once used to be the
       jank source, and now there is never more than one. */
    var previewTiles = [];   // video tiles eligible for preview, in row order
    var activeTile = null;   // the tile currently holding the turn
    var turnTimer = null;

    function isEligible(tile) {
      return !!tile._ugcVisible && !tile._ugcRefused;
    }

    /** Next eligible tile after `from`; -1 starts at the head of the row. */
    function nextTile(from) {
      var n = previewTiles.length;
      for (var k = 1; k <= n; k++) {
        var tile = previewTiles[(from + k + n) % n];
        if (isEligible(tile)) return tile;
      }
      return null;
    }

    function clearTurnTimer() {
      if (turnTimer) { window.clearTimeout(turnTimer); turnTimer = null; }
    }

    function armTurnTimer(video, ms) {
      clearTurnTimer();
      turnTimer = window.setTimeout(function () {
        turnTimer = null;
        if (activeTile && activeTile._ugcVideo === video) advance();
      }, Math.min(ms, MAX_TURN_MS));
    }

    /**
     * Clone the tile's <template> into a live <video>.
     * Idempotent — returns the existing element on repeat calls.
     */
    function ensureVideo(tile) {
      if (tile._ugcVideo) return tile._ugcVideo;

      var tpl = tile.parentNode.querySelector('[data-ugc-video]');
      if (!tpl) return null;

      var video = tpl.content.firstElementChild.cloneNode(true);
      // iOS ignores the muted *attribute* on elements created or modified
      // dynamically often enough that setting the property is not optional.
      video.muted = true;
      // The relay needs each clip to end — `ended` is what passes the turn on —
      // and it decides when one starts. Belt and braces against the template
      // regaining either attribute.
      video.removeAttribute('loop');
      video.loop = false;
      video.removeAttribute('autoplay');
      video.autoplay = false;

      video.addEventListener('ended', function () {
        if (activeTile === tile) advance();
      });
      // Once the real duration is known, replace the blind guard-rail timeout
      // with one that only fires if playback has genuinely stalled.
      video.addEventListener('loadedmetadata', function () {
        if (activeTile === tile && video.duration) {
          armTurnTimer(video, video.duration * 1000 + 1000);
        }
      });

      tile.insertBefore(video, tile.firstChild);
      tile._ugcVideo = video;
      return video;
    }

    /** Hand the tile back to its first frame, so an idle tile reads as still. */
    function rewind(tile) {
      if (!tile || !tile._ugcVideo) return;
      tile._ugcVideo.pause();
      try { tile._ugcVideo.currentTime = 0; } catch (e) { /* not seekable yet */ }
    }

    function playTile(tile) {
      if (reduceMotion || expanded || !tile) return;

      var video = ensureVideo(tile);
      if (!video) return;

      activeTile = tile;
      try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
      armTurnTimer(video, TURN_TIMEOUT_MS);

      var attempt = video.play();
      if (attempt && attempt.then) {
        attempt.then(function () {
          // A refusal earlier (see below) may have promoted the badge; a
          // successful play means the tile is a live preview again.
          tile.classList.remove('is-poster-only');
          tile._ugcRefused = false;
        }).catch(function (err) {
          // AbortError is not a refusal: the pending play() was interrupted by a
          // pause() — the turn moved on (tile scrolled out, layout shifted, the
          // expander opened) before the clip started. Whoever interrupted owns
          // the turn now. Treating it as a refusal used to strip the first
          // tile's video and skip it until a tap, so on phones — where the strip
          // is scrolled into rather than on screen at load — the first clip
          // never played and the relay only cycled the later tiles.
          if (err && err.name === 'AbortError') return;
          // Low Power Mode, data saver, or a policy we don't control. Keep the
          // poster up and turn the corner indicator into a real play button;
          // clicking still opens the expander inside the user's gesture.
          // Don't pass the turn on — the next tile would be refused too. The
          // relay restarts from the head of the row on the first gesture.
          tile.classList.add('is-poster-only');
          tile._ugcRefused = true;
          if (tile._ugcVideo) {
            tile.removeChild(tile._ugcVideo);
            tile._ugcVideo = null;
          }
          if (activeTile === tile) { clearTurnTimer(); activeTile = null; }
          armRetry();
        });
      }
    }

    /** Pass the turn from the current tile to the next one along the row. */
    function advance() {
      clearTurnTimer();
      if (reduceMotion || expanded) return;

      var prev = activeTile;
      var next = nextTile(prev ? previewTiles.indexOf(prev) : -1);
      // A lone eligible tile is its own successor, and simply plays again.
      if (prev && prev !== next) rewind(prev);
      activeTile = next;
      if (next) playTile(next);
    }

    function startSequence() {
      if (reduceMotion || expanded || activeTile) return;
      var first = nextTile(-1);
      if (first) playTile(first);
    }

    function stopSequence() {
      clearTurnTimer();
      if (activeTile) rewind(activeTile);
    }

    /**
     * Some autoplay policies refuse even muted play() until the page has had a
     * user gesture. Retry the refused tiles once, on the first interaction.
     */
    var retryArmed = false;
    function armRetry() {
      if (retryArmed) return;
      retryArmed = true;
      var handler = function () {
        document.removeEventListener('pointerdown', handler, true);
        document.removeEventListener('keydown', handler, true);
        previewTiles.forEach(function (tile) { tile._ugcRefused = false; });
        startSequence();
      };
      document.addEventListener('pointerdown', handler, true);
      document.addEventListener('keydown', handler, true);
    }

    function pauseAll() { stopSequence(); }

    /* Coming back from the expander: the tile that held the turn keeps it if it
       is still on screen, otherwise the relay starts over from the head. */
    function resumeAll() {
      if (reduceMotion || expanded) return;
      if (activeTile && isEligible(activeTile)) {
        playTile(activeTile);
      } else {
        activeTile = null;
        startSequence();
      }
    }

    var observer = hasIO ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var tile = entry.target.querySelector('[data-ugc-tile]');
        if (!tile || tile.dataset.kind !== 'video') return;

        // Genuinely on screen, not just near it. With a pre-margin the relay
        // started while the strip was still below the fold on mobile, so the
        // first clip spent its turn unseen (iOS may also hold off-screen video)
        // and the visitor arrived mid-row. preload="none" means a margin buys
        // no warm-up anyway.
        var visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        tile._ugcVisible = visible;
        if (visible) {
          startSequence();
        } else if (activeTile === tile) {
          // Scrolled away mid-turn: hand it straight on rather than play to an
          // empty room. advance() rewinds the outgoing tile itself.
          advance();
        }
      });
    }, { threshold: [0, 0.5] }) : null;

    tiles.forEach(function (tile) {
      if (tile.dataset.kind === 'video') {
        if (reduceMotion || saveData || !observer) {
          // Poster-only by policy, not by failure. Still clickable.
          tile.classList.add('is-poster-only');
        } else {
          previewTiles.push(tile);
          observer.observe(tile.parentNode);
        }
      }

      tile.addEventListener('click', function () {
        // Array position, not data-index: entries the Liquid skipped leave gaps.
        open(tiles.indexOf(tile), tile);
      });
    });

    /* ------------------------------------------------------------------ */
    /* Expander                                                            */
    /* ------------------------------------------------------------------ */

    var stage = expander.querySelector('[data-ugc-stage]');
    var track = expander.querySelector('[data-ugc-track]');
    var toggle = expander.querySelector('[data-ugc-toggle]');
    var mute = expander.querySelector('[data-ugc-mute]');
    var rail = expander.querySelector('[data-ugc-rail]');
    var closeBtn = expander.querySelector('[data-ugc-close]');

    /* Rail: capped at 4 visible (rows on desktop, columns on phones — see the
       snippet's CSS), these buttons scroll the rest into view. Hidden entries
       mean overflow stays hidden/clipped on the rail itself on desktop, so —
       same as the strip's arrows — these are the only way across there; each
       hides itself once there's nothing further that way. Phones also get
       native touch-drag scrolling on the rail (overflow-x: auto), so the
       arrows are a convenience, not the only way across. A ResizeObserver
       (not just 'resize') catches the rail's own size animating in when the
       expander first opens. */
    var railHorizontalMQ = window.matchMedia('(max-width: 749px)');
    var railUpBtn = expander.querySelector('[data-ugc-rail-up]');
    var railDownBtn = expander.querySelector('[data-ugc-rail-down]');
    if (railUpBtn || railDownBtn) {
      var updateRailNav = function () {
        if (railHorizontalMQ.matches) {
          var maxX = rail.scrollWidth - rail.clientWidth;
          if (railDownBtn) railDownBtn.hidden = maxX <= 1 || rail.scrollLeft >= maxX - 1;
          if (railUpBtn) railUpBtn.hidden = rail.scrollLeft <= 1;
        } else {
          var maxY = rail.scrollHeight - rail.clientHeight;
          if (railDownBtn) railDownBtn.hidden = maxY <= 1 || rail.scrollTop >= maxY - 1;
          if (railUpBtn) railUpBtn.hidden = rail.scrollTop <= 1;
        }
      };
      if (railDownBtn) {
        railDownBtn.addEventListener('click', function () {
          var by = railHorizontalMQ.matches ? { left: rail.clientWidth } : { top: rail.clientHeight };
          by.behavior = reduceMotion ? 'auto' : 'smooth';
          rail.scrollBy(by);
        });
      }
      if (railUpBtn) {
        railUpBtn.addEventListener('click', function () {
          var by = railHorizontalMQ.matches ? { left: -rail.clientWidth } : { top: -rail.clientHeight };
          by.behavior = reduceMotion ? 'auto' : 'smooth';
          rail.scrollBy(by);
        });
      }
      rail.addEventListener('scroll', updateRailNav, { passive: true });
      if ('ResizeObserver' in window) {
        new ResizeObserver(updateRailNav).observe(rail);
      } else {
        window.addEventListener('resize', updateRailNav);
      }
      if (railHorizontalMQ.addEventListener) railHorizontalMQ.addEventListener('change', updateRailNav);
      updateRailNav();
    }

    var currentIndex = -1;
    var currentVideo = null;
    var currentHost = null;
    var originTile = null;
    var closeTimer = null;
    var railButtons = [];
    var slides = [];
    var programmaticScroll = false;
    var scrollTimer = null;

    function setIcon(button, name) {
      var svgs = button.querySelectorAll('svg[data-icon]');
      Array.prototype.forEach.call(svgs, function (svg) {
        svg.hidden = svg.getAttribute('data-icon') !== name;
      });
    }

    function setStageRatio(w, h) {
      if (!w || !h) return;
      var ratio = w / h;
      // Keep the stage inside sane bounds so a stray landscape clip or an
      // extreme panorama doesn't blow the column apart.
      if (ratio < 9 / 16) ratio = 9 / 16;
      if (ratio > 16 / 9) ratio = 16 / 9;
      stage.style.aspectRatio = String(ratio);
    }

    function setPausedUI(paused) {
      toggle.classList.toggle('is-paused', paused);
      toggle.setAttribute('aria-label', paused ? 'Play' : 'Pause');
    }

    /** Build the rail and the track's slides from the strip's tiles. */
    function build() {
      if (railButtons.length) return;
      tiles.forEach(function (tile, i) {
        var img = tile.querySelector('.product-ugc__poster');

        // Slide: poster only until it becomes current.
        var slide = document.createElement('div');
        slide.className = 'product-ugc__slide';
        slide.setAttribute('aria-hidden', 'true');
        if (img) {
          var poster = document.createElement('img');
          poster.className = 'product-ugc__slide-poster';
          poster.alt = '';
          poster.decoding = 'async';
          poster.loading = 'lazy';
          poster.src = tile.dataset.poster || img.currentSrc || img.src;
          slide.appendChild(poster);
        }
        track.appendChild(slide);
        slides.push(slide);

        // Rail button.
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-ugc__tile';
        btn.setAttribute('aria-label',
          (tile.dataset.kind === 'video' ? 'Show video: ' : 'Show image: ') + (tile.dataset.alt || ''));
        if (img) {
          var clone = img.cloneNode(true);
          clone.setAttribute('sizes', '(max-width: 749px) 20vw, 64px');
          clone.setAttribute('loading', 'lazy');
          btn.appendChild(clone);
        }
        var indicator = tile.querySelector('.product-ugc__indicator');
        if (indicator) btn.appendChild(indicator.cloneNode(true));
        btn.addEventListener('click', function () {
          if (i !== currentIndex) show(i);
        });
        li.appendChild(btn);
        rail.appendChild(li);
        railButtons.push(btn);
      });
    }

    function teardownMedia() {
      if (currentVideo) {
        currentVideo.pause();
        // Multiple <source> children: removing src alone doesn't release them.
        while (currentVideo.firstChild) currentVideo.removeChild(currentVideo.firstChild);
        currentVideo.removeAttribute('src');
        currentVideo.load();
        if (currentVideo.parentNode) currentVideo.parentNode.removeChild(currentVideo);
        currentVideo = null;
      }
      if (currentHost) {
        var full = currentHost.querySelector('img:not(.product-ugc__slide-poster)');
        if (full) currentHost.removeChild(full);
        currentHost.setAttribute('aria-hidden', 'true');
        currentHost = null;
      }
      mute.hidden = true;
      toggle.hidden = true;
      setPausedUI(false);
    }

    function scrollToSlide(i, smooth) {
      var slide = slides[i];
      if (!slide) return;
      var left = slide.offsetLeft;
      if (Math.abs(track.scrollLeft - left) < 2) return;
      programmaticScroll = true;
      try {
        track.scrollTo({ left: left, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      } catch (e) {
        track.scrollLeft = left;
      }
      // An instant scroll that the browser clamped (the track is still
      // collapsed mid-open, so there is nothing to scroll yet) never fires a
      // scroll event, and would leave the flag stuck. Clear it ourselves so
      // the scroll handler isn't ignored; whoever called us retries later.
      if (!(smooth && !reduceMotion) && Math.abs(track.scrollLeft - left) >= 2) {
        programmaticScroll = false;
      }
    }

    // While the expander animates open from 0fr the stage is 0 x 0 (its
    // width follows its height through aspect-ratio), so the track has no
    // scroll range and scrollTo() is clamped to 0. An opening click on any
    // slide but the first therefore showed slide 1 with the clicked video
    // playing off-screen to the right. Keep landing on the wanted slide every
    // time the track changes size — the open tween, the stage ratio changing
    // once the clip's metadata arrives — until it actually sticks or the user
    // swipes elsewhere. Timer fallback for engines without ResizeObserver.
    var landIndex = -1;
    var landTimer = null;
    function landed(i) {
      var slide = slides[i];
      // > 0 alone isn't enough: early in the opening tween the track is only a
      // few px wide, and at that size slide 1's offset (1 * that width) can be
      // < 2px from a scrollLeft of 0 by coincidence — a false "already there"
      // that stops the retries before the track reaches its real size. Slide 2
      // and 3's offsets clear 2px sooner, so only the second tile ever showed
      // this. Require a width too wide for that to happen.
      return !!slide && track.clientWidth > 40 && Math.abs(track.scrollLeft - slide.offsetLeft) < 2;
    }
    function tryLand() {
      if (landIndex < 0) return;
      if (!expanded || landIndex !== currentIndex) { landIndex = -1; return; }
      if (!landed(landIndex)) scrollToSlide(landIndex, false);
      if (landed(landIndex)) landIndex = -1;
    }
    var landObserver = 'ResizeObserver' in window ? new ResizeObserver(tryLand) : null;
    if (landObserver) landObserver.observe(track);
    function landOnSlide(i) {
      landIndex = i;
      tryLand();
      if (landTimer) window.clearTimeout(landTimer);
      // Belt and braces: after the tween has certainly finished, one last go.
      landTimer = window.setTimeout(function () {
        landTimer = null;
        tryLand();
      }, reduceMotion ? 0 : TRANSITION_MS + 100);
    }

    /* Same landing problem, vertically, for the rail: opening (or jumping
       straight to) an entry past the 4th otherwise leaves its rail button
       highlighted below the fold, off the visible page. Rows page in fixed
       groups of 4 (matching the CSS), so entry i's page is a simple floor
       division — entry 5 (index 5) lands on page 1 (indices 4-6), which with
       only 7 entries total is 2 of 3 rows on that page, i.e. the middle one. */
    var RAIL_PAGE_SIZE = 4;
    var railLandIndex = -1;
    var railLandTimer = null;
    function railLanded(i) {
      if (!rail) return false;
      // Same >40 guard as landed() above, and for the same reason.
      if (railHorizontalMQ.matches) {
        if (rail.clientWidth <= 40) return false;
        return Math.abs(rail.scrollLeft - Math.floor(i / RAIL_PAGE_SIZE) * rail.clientWidth) < 2;
      }
      if (rail.clientHeight <= 40) return false;
      return Math.abs(rail.scrollTop - Math.floor(i / RAIL_PAGE_SIZE) * rail.clientHeight) < 2;
    }
    function tryLandRail() {
      if (railLandIndex < 0) return;
      if (!expanded || railLandIndex !== currentIndex) { railLandIndex = -1; return; }
      if (!railLanded(railLandIndex)) {
        if (railHorizontalMQ.matches) {
          rail.scrollTo({ left: Math.floor(railLandIndex / RAIL_PAGE_SIZE) * rail.clientWidth, behavior: 'auto' });
        } else {
          rail.scrollTo({ top: Math.floor(railLandIndex / RAIL_PAGE_SIZE) * rail.clientHeight, behavior: 'auto' });
        }
      }
      if (railLanded(railLandIndex)) railLandIndex = -1;
    }
    var railObserver = rail && 'ResizeObserver' in window ? new ResizeObserver(tryLandRail) : null;
    if (railObserver) railObserver.observe(rail);
    function landRailOnSlide(i) {
      if (!rail) return;
      railLandIndex = i;
      tryLandRail();
      if (railLandTimer) window.clearTimeout(railLandTimer);
      railLandTimer = window.setTimeout(function () {
        railLandTimer = null;
        tryLandRail();
      }, reduceMotion ? 0 : TRANSITION_MS + 100);
    }

    function tryPlay(video) {
      var attempt = video.play();
      if (!attempt || !attempt.then) return;
      attempt.then(function () {
        setPausedUI(false);
      }).catch(function () {
        if (currentVideo !== video) return;
        // Unmuted playback without a fresh gesture (e.g. arrived here by
        // swiping) is refused on iOS: fall back to muted and try once more.
        if (!video.muted) {
          video.muted = true;
          mute.setAttribute('aria-pressed', 'true');
          mute.setAttribute('aria-label', 'Unmute');
          setIcon(mute, 'muted');
          tryPlay(video);
          return;
        }
        // Refused outright (Low Power Mode): show the play glyph as a tap target.
        setPausedUI(true);
      });
    }

    /**
     * Mount entry `i` in its slide. Called synchronously from click handlers so
     * that video.play() still counts as gesture-initiated on iOS. `fromScroll`
     * means the user swiped there, so don't scroll the track again.
     */
    function show(i, fromScroll) {
      var tile = tiles[i];
      var slide = slides[i];
      if (!tile || !slide) return;

      teardownMedia();
      currentIndex = i;
      currentHost = slide;
      slide.removeAttribute('aria-hidden');

      railButtons.forEach(function (btn, j) {
        if (j === i) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
      tiles.forEach(function (t, j) {
        t.setAttribute('aria-expanded', j === i ? 'true' : 'false');
      });

      if (!fromScroll) scrollToSlide(i, expanded);
      // Only land the rail here for switches while already open (rail clicks,
      // keyboard nav, swipes). On a fresh open `expanded` is still false at
      // this point — open() flips it true right after show() returns — so
      // tryLandRail's own staleness guard would cancel the request before the
      // retries ever ran. open() re-fires this itself once expanded is true.
      if (expanded) landRailOnSlide(i);

      if (tile.dataset.kind === 'video') {
        var video = document.createElement('video');
        video.muted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('loop', '');
        video.setAttribute('preload', 'auto');
        video.setAttribute('disablepictureinpicture', '');
        video.setAttribute('disableremoteplayback', '');
        video.setAttribute('controlslist', 'nodownload noplaybackrate');
        if (tile.dataset.poster) video.setAttribute('poster', tile.dataset.poster);
        video.setAttribute('aria-label', tile.dataset.alt || '');

        // 720p first; the original (largest) mp4 only as a network-error fallback.
        [tile.dataset.videoHd, tile.dataset.videoMax].forEach(function (url) {
          if (!url) return;
          var source = document.createElement('source');
          source.src = url;
          source.type = 'video/mp4';
          video.appendChild(source);
        });

        slide.appendChild(video);
        currentVideo = video;
        mute.hidden = false;
        toggle.hidden = false;

        video.addEventListener('loadedmetadata', function () {
          setStageRatio(video.videoWidth, video.videoHeight);
        });
        video.addEventListener('play', function () { setPausedUI(false); });
        video.addEventListener('pause', function () { setPausedUI(true); });

        // Carry the user's sound choice across entries; default muted.
        video.muted = mute.getAttribute('aria-pressed') === 'true';
        tryPlay(video);
      } else {
        var img = document.createElement('img');
        img.alt = tile.dataset.alt || '';
        if (tile.dataset.fullSrcset) img.srcset = tile.dataset.fullSrcset;
        img.sizes = '(max-width: 749px) 100vw, 520px';
        img.src = tile.dataset.full || tile.dataset.poster || '';
        img.decoding = 'async';
        img.addEventListener('load', function () {
          setStageRatio(img.naturalWidth, img.naturalHeight);
        });
        slide.appendChild(img);
      }
    }

    function open(i, tile) {
      if (closeTimer) { window.clearTimeout(closeTimer); closeTimer = null; }
      build();

      var wasExpanded = expanded;
      originTile = tile || tiles[i];
      pauseAll();

      if (!wasExpanded) {
        expander.hidden = false;
        root.classList.add('is-expanded');
      }
      show(i);
      expanded = true;

      if (!wasExpanded) {
        // The element just went display:none -> grid. Force a style/layout
        // pass so the 0fr start value is computed, then flip the class on a
        // separate task so the tween runs. (A timeout rather than rAF: rAF is
        // deferred in background/throttled tabs, a timeout is not.)
        void expander.offsetHeight;
        window.setTimeout(function () {
          if (!expanded) return;
          expander.classList.add('is-open');
          // Land on the slide as soon as the track has room to scroll.
          landOnSlide(i);
          landRailOnSlide(i);
        }, 20);
        window.setTimeout(function () {
          if (!expanded) return;
          try {
            expander.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
          } catch (e) { /* older engines */ }
        }, reduceMotion ? 0 : TRANSITION_MS);
        closeBtn.focus();
      }
    }

    function close() {
      if (!expanded) return;
      expanded = false;

      teardownMedia();
      tiles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      expander.classList.remove('is-open');
      root.classList.remove('is-expanded');

      var finish = function () {
        closeTimer = null;
        expander.hidden = true;
        currentIndex = -1;
        resumeAll();
      };
      if (reduceMotion) finish();
      else closeTimer = window.setTimeout(finish, TRANSITION_MS);

      if (originTile && originTile.focus) originTile.focus();
    }

    /* Controls */

    closeBtn.addEventListener('click', close);

    toggle.addEventListener('click', function () {
      if (!currentVideo) return;
      if (currentVideo.paused) tryPlay(currentVideo);
      else currentVideo.pause();
    });

    mute.addEventListener('click', function () {
      var nowMuted = mute.getAttribute('aria-pressed') !== 'true';
      mute.setAttribute('aria-pressed', nowMuted ? 'true' : 'false');
      mute.setAttribute('aria-label', nowMuted ? 'Unmute' : 'Mute');
      setIcon(mute, nowMuted ? 'muted' : 'unmuted');
      if (currentVideo) {
        currentVideo.muted = nowMuted;
        // Unmuting inside the gesture: also make sure it is actually playing.
        if (!nowMuted && currentVideo.paused) tryPlay(currentVideo);
      }
    });

    /* Swipe: when the track settles on a different slide, make it current. */
    track.addEventListener('scroll', function () {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(function () {
        scrollTimer = null;
        var w = track.clientWidth;
        if (!w) return;
        var idx = Math.round(track.scrollLeft / w);
        idx = Math.min(slides.length - 1, Math.max(0, idx));
        if (programmaticScroll) {
          if (idx === currentIndex) programmaticScroll = false;
          return;
        }
        if (idx !== currentIndex && expanded) { landIndex = -1; show(idx, true); }
      }, 80);
    }, { passive: true });

    /* Keyboard: Escape closes; arrows walk the entries. */
    expander.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      var delta = 0;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') delta = 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') delta = -1;
      else if (e.key === 'Home') delta = -railButtons.length;
      else if (e.key === 'End') delta = railButtons.length;
      if (!delta || !railButtons.length) return;

      e.preventDefault();
      var next = Math.min(railButtons.length - 1, Math.max(0, currentIndex + delta));
      if (next === currentIndex) return;
      if (rail.contains(document.activeElement)) railButtons[next].focus();
      show(next);
    });
  }
})();
